import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactMarkdown from 'react-markdown';

function CheckpointContent({ data }) {
  const [expandedSections, setExpandedSections] = useState(new Set(['Reports', 'Code']));
  const [expandedItems, setExpandedItems] = useState(new Set());

  if (!data) {
    return <div className="text-sm text-gray-500">No checkpoint data available</div>;
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const toggleItem = (item) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(item)) {
        next.delete(item);
      } else {
        next.add(item);
      }
      return next;
    });
  };

  const formatReportContent = (report) => {
    if (report.format === 'json') {
      return JSON.stringify(report.content, null, 2);
    }
    return report.content;
  };

  const getReportBorderColor = (type) => {
    switch (type) {
      case 'performance':
        return 'border-emerald-500 dark:border-emerald-600';
      case 'analysis':
        return 'border-blue-500 dark:border-blue-600';
      case 'configuration':
        return 'border-amber-500 dark:border-amber-600';
      default:
        return 'border-gray-300 dark:border-gray-600';
    }
  };

  return (
    <div className="space-y-4 text-xs">
      {/* Header */}
      <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1">
          {data.phase?.replace(/_/g, ' ')}
        </h3>
        {data.timestamp && (
          <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
            {new Date(data.timestamp).toLocaleString()}
          </p>
        )}
      </div>

      {/* Reports */}
      {data.reports && data.reports.length > 0 && (
        <CollapsibleSection
          title="Reports"
          count={data.reports.length}
          isExpanded={expandedSections.has('Reports')}
          onToggle={() => toggleSection('Reports')}
        >
          <div className="space-y-3">
            {data.reports.map((report, idx) => {
              const isExpanded = expandedItems.has(report.name);
              return (
                <div key={idx} className={`border-l-2 ${getReportBorderColor(report.type)} pl-3`}>
                  <button
                    onClick={() => toggleItem(report.name)}
                    className="flex items-baseline justify-between w-full text-left group"
                  >
                    <div className="flex items-center gap-1">
                      {isExpanded ? (
                        <ChevronDownIcon className="w-3 h-3 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="w-3 h-3 text-gray-400" />
                      )}
                      <span className="text-xs font-medium text-gray-900 dark:text-white group-hover:text-gray-700 dark:group-hover:text-gray-200">
                        {report.title}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {report.type}
                    </span>
                  </button>

                  {isExpanded && (
                    <div className="mt-2 bg-gray-50 dark:bg-gray-900/50 rounded border border-gray-200 dark:border-gray-700 overflow-hidden">
                      {report.format === 'json' ? (
                        <SyntaxHighlighter
                          language="json"
                          style={vscDarkPlus}
                          customStyle={{
                            margin: 0,
                            padding: '0.5rem',
                            fontSize: '0.7rem',
                            maxHeight: '300px',
                            overflow: 'auto',
                            background: 'transparent'
                          }}
                        >
                          {formatReportContent(report)}
                        </SyntaxHighlighter>
                      ) : report.format === 'markdown' ? (
                        <div className="prose prose-xs dark:prose-invert max-w-none prose-headings:font-semibold prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-li:text-gray-700 dark:prose-li:text-gray-300 p-2">
                          <ReactMarkdown>{report.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono p-2">
                          {report.content}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CollapsibleSection>
      )}

      {/* Code */}
      {data.code && data.code.length > 0 && (
        <CollapsibleSection
          title="Code"
          count={data.code.length}
          isExpanded={expandedSections.has('Code')}
          onToggle={() => toggleSection('Code')}
        >
          <div className="space-y-2">
            {data.code.map((codeItem, idx) => {
              const isExpanded = expandedItems.has(codeItem.name);
              return (
                <div key={idx} className="border-l-2 border-blue-500 dark:border-blue-600 pl-3">
                  <button
                    onClick={() => toggleItem(codeItem.name)}
                    className="flex items-baseline justify-between w-full text-left group"
                  >
                    <div className="flex items-center gap-1">
                      {isExpanded ? (
                        <ChevronDownIcon className="w-3 h-3 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="w-3 h-3 text-gray-400" />
                      )}
                      <span className="font-mono text-gray-900 dark:text-white text-xs group-hover:text-blue-600 dark:group-hover:text-blue-400">
                        {codeItem.title}
                      </span>
                    </div>
                    <span className="text-gray-500 dark:text-gray-400 font-mono text-xs">
                      {codeItem.line_count} lines
                    </span>
                  </button>

                  {isExpanded && codeItem.content && (
                    <div className="mt-2 rounded border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <SyntaxHighlighter
                        language={codeItem.language}
                        style={vscDarkPlus}
                        customStyle={{
                          margin: 0,
                          padding: '0.5rem',
                          fontSize: '0.7rem',
                          maxHeight: '400px',
                          overflow: 'auto'
                        }}
                        showLineNumbers
                      >
                        {codeItem.content}
                      </SyntaxHighlighter>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CollapsibleSection>
      )}

      {/* Artifacts */}
      {data.artifacts && data.artifacts.length > 0 && (
        <CollapsibleSection
          title="Artifacts"
          count={data.artifacts.length}
          isExpanded={expandedSections.has('Artifacts')}
          onToggle={() => toggleSection('Artifacts')}
        >
          <div className="space-y-1">
            {data.artifacts.map((artifact, idx) => (
              <div key={idx} className="flex items-baseline justify-between py-1 px-2 rounded hover:bg-gray-50 dark:hover:bg-slate-700/30">
                <span className="text-xs text-gray-700 dark:text-gray-300 font-mono">
                  {artifact.name}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                  {formatBytes(artifact.size_bytes)}
                </span>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}

function CollapsibleSection({ title, count, isExpanded, onToggle, children }) {
  return (
    <div>
      <button
        onClick={onToggle}
        className="flex items-center gap-2 w-full text-left mb-2 group"
      >
        {isExpanded ? (
          <ChevronDownIcon className="w-3 h-3 text-gray-400" />
        ) : (
          <ChevronRightIcon className="w-3 h-3 text-gray-400" />
        )}
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
          {title}
        </span>
        <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
          ({count})
        </span>
      </button>
      {isExpanded && <div className="ml-5">{children}</div>}
    </div>
  );
}

// Helper function
function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

export default CheckpointContent;